import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductBrief, createProductBrief, updateProductBrief } from '../services/api';
import Modal from '../components/Modal';
import {
  Box, Typography, Button, Paper, Stack, Chip, TextField, Alert,
  CircularProgress, DialogActions, Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// ── Guided configuration sections ──────────────────

const ARRAY_SECTIONS = [
  { key: 'keyDifferentiators', title: 'Key Differentiators', icon: '⚡', description: 'What sets your product apart from the rest?' },
  { key: 'competitiveAdvantages', title: 'Competitive Advantages', icon: '🏆', description: 'Sustainable advantages your product holds over competitors.' },
  { key: 'competitorPositioning', title: 'Competitor Positioning', icon: '🎯', description: 'How you position against specific competitors.' },
  { key: 'useCases', title: 'Use Cases', icon: '💡', description: 'Primary use cases your product addresses.' },
  { key: 'disqualifiers', title: 'Disqualifiers', icon: '🚫', description: 'Criteria that disqualify a lead from being a good fit.' },
  { key: 'messagingRules', title: 'Messaging Rules', icon: '📏', description: 'Rules AI must follow when generating outreach messaging.' },
  { key: 'approvedClaims', title: 'Approved Claims', icon: '✅', description: 'Pre-approved statements AI is allowed to make.' },
  { key: 'forbiddenClaims', title: 'Forbidden Claims', icon: '❌', description: 'Statements AI must never use in messaging.' },
];

function safeParse(val, fallback) {
  if (val === null || val === undefined) return fallback;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// ── Reusable list item component ──────────────────

function ArrayListSection({ title, icon, description, items, onAdd, onAddMultiple, onEdit, onDelete }) {
  return (
    <Paper sx={{ p: 3, mb: 2.5 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{icon}</span> {title}
            <Chip size="small" label={items.length} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={onAddMultiple}>+ Add Multiple</Button>
          <Button variant="contained" size="small" onClick={onAdd}>+ Add</Button>
        </Stack>
      </Stack>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
          No items added yet
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {items.map((item, idx) => (
            <Paper key={idx} variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1, mr: 1.5 }}>{item}</Typography>
              <Stack direction="row" spacing={0.5}>
                <Button variant="outlined" size="small" onClick={() => onEdit(idx)}>Edit</Button>
                <Button variant="outlined" color="error" size="small" onClick={() => onDelete(idx)}>Delete</Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

// ── Key-Value list section ──────────────────

function KeyValueSection({ title, icon, description, entries, onAdd, onEdit, onDelete }) {
  return (
    <Paper sx={{ p: 3, mb: 2.5 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{icon}</span> {title}
            <Chip size="small" label={entries.length} />
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{description}</Typography>
        </Box>
        <Button variant="contained" size="small" onClick={onAdd}>+ Add</Button>
      </Stack>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2.5 }}>
          No entries added yet
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {entries.map(([k, v], idx) => (
            <Paper key={idx} variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, mb: 0.5 }}>
              <Box sx={{ flex: 1, mr: 1.5 }}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{k}</Typography>
                <Typography variant="body2" component="span" sx={{ mx: 1, color: 'text.disabled' }}>—</Typography>
                <Typography variant="body2" component="span" color="text.secondary">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Button variant="outlined" size="small" onClick={() => onEdit(idx)}>Edit</Button>
                <Button variant="outlined" color="error" size="small" onClick={() => onDelete(idx)}>Delete</Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

// ── Main Page ──────────────────

export default function MarketingBrainPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Form state ──
  const [name, setName] = useState('');
  const [industryFocus, setIndustryFocus] = useState('');
  const [description, setDescription] = useState('');
  const [valueProposition, setValueProposition] = useState('');
  const [elevatorPitch, setElevatorPitch] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  // Array fields
  const [arrays, setArrays] = useState({
    keyDifferentiators: [],
    competitiveAdvantages: [],
    competitorPositioning: [],
    useCases: [],
    disqualifiers: [],
    messagingRules: [],
    approvedClaims: [],
    forbiddenClaims: [],
  });

  // Object fields (key-value)
  const [targetMarket, setTargetMarket] = useState([]);   // [[key, value], ...]
  const [messagingLibrary, setMessagingLibrary] = useState([]);

  // ── Modal state ──
  const [itemModal, setItemModal] = useState(null); // { type: 'array'|'kv', section, editIdx?, value }
  const [modalValue, setModalValue] = useState('');
  const [modalKey, setModalKey] = useState('');

  // ── Load existing brief ──
  const loadBrief = useCallback(async () => {
    try {
      const { data } = await getProductBrief(id);
      setName(data.name || '');
      setIndustryFocus(data.industryFocus || '');
      setDescription(data.description || '');
      setValueProposition(data.valueProposition || '');
      setElevatorPitch(data.elevatorPitch || '');
      setCustomInstructions(data.customInstructions || '');

      // Array fields
      const newArrays = {};
      for (const s of ARRAY_SECTIONS) {
        newArrays[s.key] = safeParse(data[s.key], []);
      }
      setArrays(newArrays);

      // Object fields → entry pairs
      const tm = safeParse(data.targetMarket, {});
      setTargetMarket(Object.entries(tm));
      const ml = safeParse(data.messagingLibrary, {});
      setMessagingLibrary(Object.entries(ml));
    } catch {
      setError('Failed to load product brief');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadBrief();
  }, [isNew, loadBrief]);

  // ── Array item handlers ──
  const openAddItem = (sectionKey) => {
    setModalValue('');
    setItemModal({ type: 'array', section: sectionKey, editIdx: null });
  };

  const openAddMultipleItems = (sectionKey) => {
    setModalValue('');
    setItemModal({ type: 'array-multi', section: sectionKey });
  };

  const handleSaveMultipleItems = () => {
    if (!modalValue.trim()) return;
    const lines = modalValue.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    const { section } = itemModal;
    setArrays(prev => ({
      ...prev,
      [section]: [...prev[section], ...lines],
    }));
    setItemModal(null);
  };

  const openEditItem = (sectionKey, idx) => {
    setModalValue(arrays[sectionKey][idx] || '');
    setItemModal({ type: 'array', section: sectionKey, editIdx: idx });
  };

  const handleDeleteItem = (sectionKey, idx) => {
    setArrays(prev => ({
      ...prev,
      [sectionKey]: prev[sectionKey].filter((_, i) => i !== idx),
    }));
  };

  const handleSaveItem = () => {
    if (!modalValue.trim()) return;
    const { section, editIdx } = itemModal;
    setArrays(prev => {
      const arr = [...prev[section]];
      if (editIdx !== null) {
        arr[editIdx] = modalValue.trim();
      } else {
        arr.push(modalValue.trim());
      }
      return { ...prev, [section]: arr };
    });
    setItemModal(null);
  };

  // ── Key-Value handlers ──
  const openAddKV = (which) => {
    setModalKey('');
    setModalValue('');
    setItemModal({ type: 'kv', section: which, editIdx: null });
  };

  const openEditKV = (which, idx) => {
    const entries = which === 'targetMarket' ? targetMarket : messagingLibrary;
    setModalKey(entries[idx][0] || '');
    setModalValue(typeof entries[idx][1] === 'object' ? JSON.stringify(entries[idx][1]) : String(entries[idx][1]));
    setItemModal({ type: 'kv', section: which, editIdx: idx });
  };

  const handleDeleteKV = (which, idx) => {
    const setter = which === 'targetMarket' ? setTargetMarket : setMessagingLibrary;
    setter(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveKV = () => {
    if (!modalKey.trim()) return;
    const { section, editIdx } = itemModal;
    const setter = section === 'targetMarket' ? setTargetMarket : setMessagingLibrary;
    setter(prev => {
      const arr = [...prev];
      const val = modalValue.trim();
      if (editIdx !== null) {
        arr[editIdx] = [modalKey.trim(), val];
      } else {
        arr.push([modalKey.trim(), val]);
      }
      return arr;
    });
    setItemModal(null);
  };

  // ── Save brief ──
  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        industryFocus: industryFocus.trim(),
        description: description.trim(),
        valueProposition: valueProposition.trim(),
        elevatorPitch: elevatorPitch.trim(),
        customInstructions: customInstructions.trim() || null,
        targetMarket: JSON.stringify(Object.fromEntries(targetMarket)),
        messagingLibrary: JSON.stringify(Object.fromEntries(messagingLibrary)),
      };
      for (const s of ARRAY_SECTIONS) {
        payload[s.key] = JSON.stringify(arrays[s.key]);
      }
      if (isNew) {
        await createProductBrief(payload);
      } else {
        await updateProductBrief(id, payload);
      }
      navigate('/management?tab=briefs');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save product brief');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Back link + header */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=briefs')} sx={{ mb: 2 }}>
        Back to Management
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{isNew ? 'New Marketing Brain' : 'Edit Marketing Brain'}</Typography>
          <Typography variant="body2" color="text.secondary">Configure your product brief — this context powers all AI operations.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/management?tab=briefs')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Brief' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Section 1: Basic Information ── */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📋</span> Basic Information
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Core identity and focus of your product brief.</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth required label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Product / brand name" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth label="Industry Focus" value={industryFocus} onChange={(e) => setIndustryFocus(e.target.value)} placeholder="e.g. SaaS, Fintech, Healthcare" />
          </Grid>
        </Grid>
        <TextField fullWidth multiline rows={3} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of the product and what it does" sx={{ mt: 2 }} />
      </Paper>

      {/* ── Section 2: Value Proposition ── */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>💎</span> Value Proposition
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Core messaging that communicates your product's value.</Typography>
        <TextField fullWidth multiline rows={3} label="Value Proposition" value={valueProposition} onChange={(e) => setValueProposition(e.target.value)} placeholder="The core value your product delivers to customers" />
        <TextField fullWidth multiline rows={3} label="Elevator Pitch" value={elevatorPitch} onChange={(e) => setElevatorPitch(e.target.value)} placeholder="A concise pitch you'd deliver in 30 seconds" sx={{ mt: 2 }} />
        <TextField
          fullWidth multiline rows={4}
          label="Custom Instructions (AI personalization)"
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder="Free-form rules for personalizing outbound emails. e.g. 'Always reference the prospect's industry. Avoid corporate buzzwords. Sign off casually.'"
          helperText={`Injected into AI-generation prompts as {{CUSTOM_INSTRUCTIONS}}. Max 4000 chars.`}
          inputProps={{ maxLength: 4000 }}
          sx={{ mt: 2 }}
        />
      </Paper>

      {/* ── Section 3: Target Market (Key-Value) ── */}
      <KeyValueSection
        title="Target Market"
        icon="🎯"
        description="Define your target market segments, geographies, and characteristics."
        entries={targetMarket}
        onAdd={() => openAddKV('targetMarket')}
        onEdit={(idx) => openEditKV('targetMarket', idx)}
        onDelete={(idx) => handleDeleteKV('targetMarket', idx)}
      />

      {/* ── Array list sections ── */}
      {ARRAY_SECTIONS.map((s) => (
        <ArrayListSection
          key={s.key}
          title={s.title}
          icon={s.icon}
          description={s.description}
          items={arrays[s.key]}
          onAdd={() => openAddItem(s.key)}
          onAddMultiple={() => openAddMultipleItems(s.key)}
          onEdit={(idx) => openEditItem(s.key, idx)}
          onDelete={(idx) => handleDeleteItem(s.key, idx)}
        />
      ))}

      {/* ── Messaging Library (Key-Value) ── */}
      <KeyValueSection
        title="Messaging Library"
        icon="📚"
        description="Pre-built messaging blocks organized by topic that AI can reference."
        entries={messagingLibrary}
        onAdd={() => openAddKV('messagingLibrary')}
        onEdit={(idx) => openEditKV('messagingLibrary', idx)}
        onDelete={(idx) => handleDeleteKV('messagingLibrary', idx)}
      />

      {/* ── Bottom save bar ── */}
      <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => navigate('/management?tab=briefs')}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Brief' : 'Save Changes'}
        </Button>
      </Paper>

      {/* ── Array Item Modal ── */}
      {itemModal && itemModal.type === 'array' && (
        <Modal
          title={itemModal.editIdx !== null ? 'Edit Item' : 'Add Item'}
          onClose={() => setItemModal(null)}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            label={ARRAY_SECTIONS.find(s => s.key === itemModal.section)?.title || 'Item'}
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            placeholder="Enter item text…"
            autoFocus
            sx={{ mb: 2 }}
          />
          <DialogActions sx={{ p: 0 }}>
            <Button variant="outlined" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveItem} disabled={!modalValue.trim()}>
              {itemModal.editIdx !== null ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* ── Array Add Multiple Modal ── */}
      {itemModal && itemModal.type === 'array-multi' && (
        <Modal
          title={`Add Multiple — ${ARRAY_SECTIONS.find(s => s.key === itemModal.section)?.title || 'Items'}`}
          onClose={() => setItemModal(null)}
        >
          <TextField
            fullWidth
            multiline
            rows={10}
            label="Items (one per line)"
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            placeholder={"Enter one item per line, e.g.:\nFirst item\nSecond item\nThird item"}
            autoFocus
            helperText={modalValue.trim() ? `${modalValue.split('\n').map(l => l.trim()).filter(Boolean).length} item(s) will be added` : 'Paste or type multiple items, each on its own line'}
            sx={{ mb: 2 }}
          />
          <DialogActions sx={{ p: 0 }}>
            <Button variant="outlined" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveMultipleItems} disabled={!modalValue.trim()}>
              Add All
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* ── Key-Value Modal ── */}
      {itemModal && itemModal.type === 'kv' && (
        <Modal
          title={itemModal.editIdx !== null ? 'Edit Entry' : 'Add Entry'}
          onClose={() => setItemModal(null)}
        >
          <TextField
            fullWidth
            size="small"
            label="Key *"
            value={modalKey}
            onChange={(e) => setModalKey(e.target.value)}
            placeholder="e.g. segment, geo, topic"
            autoFocus
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Value"
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            placeholder="Enter value…"
            sx={{ mb: 2 }}
          />
          <DialogActions sx={{ p: 0 }}>
            <Button variant="outlined" onClick={() => setItemModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveKV} disabled={!modalKey.trim()}>
              {itemModal.editIdx !== null ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </Modal>
      )}
    </Box>
  );
}
